import { useCallback, useLayoutEffect, useRef, useMemo, useState, useEffect, Children, Fragment as Fragment$1 } from 'react';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _typeof from '@babel/runtime/helpers/esm/typeof';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { useForm as useForm$1, useFieldArray, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import zgmYup, { constants, types, introspect, formatters, parsers } from '@zillow/zgm-yup';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import set from 'lodash/set';
import isEqual from 'lodash/isEqual';
import isPlainObject from 'lodash/isPlainObject';
import has from 'lodash/has';
import _toConsumableArray from '@babel/runtime/helpers/esm/toConsumableArray';
import { IconLockOutline, FormField, FormHelp, Label, AdornedInput, StyledInput, StyledAdornment, Adornment, Input, Select, StyledLabel, spaceMixin, StyledFormField, LabeledControl, Checkbox, IconCalendarOutline, DatePickerInput, DatePicker, DATE_PICKER_CHANGE_REASON, Flex, VisuallyHidden, Radio, Textarea, ToggleButtonGroup, ToggleButton, token, Combobox } from '@zillow/constellation';
import PropTypes from 'prop-types';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import uniqueId from 'lodash/uniqueId';
import _taggedTemplateLiteralLoose from '@babel/runtime/helpers/esm/taggedTemplateLiteralLoose';
import styled, { css } from 'styled-components';
import omit from 'lodash/omit';

/**
 * On focus of the input, set the autocomplete attribute on the input element to ‘chrome-off’, but do so only on focus.
 * Chrome keeps track of how many autocomplete=”chrome-off” elements exist, and if they exceed 5, it ignores the rest.
 * If we remove the autocomplete attribute on blur, then it seems to be fine.
 *
 * @param {*} ref Input reference object
 * @param {Object} props
 * @param {String} props.autoComplete
 * @param {Function} props.onFocus On focus callback invoked with the set attribute
 * @param {Function} props.onBlur On blur callback invoked with the remove attribute
 */

var useAutoComplete = function useAutoComplete(ref, _ref) {
  var autoComplete = _ref.autoComplete,
      onFocus = _ref.onFocus,
      onBlur = _ref.onBlur;
  var noAutoCompleteOnBlur = useCallback(function () {
    var _ref$current;

    (_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.removeAttribute('autocomplete');

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    onBlur === null || onBlur === void 0 ? void 0 : onBlur.apply(void 0, args);
  }, [ref, onBlur]);
  var noAutoCompleteOnFocus = useCallback(function () {
    ref.current.setAttribute('autocomplete', 'chrome-off');

    for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }

    onFocus === null || onFocus === void 0 ? void 0 : onFocus.apply(void 0, args);
  }, [ref, onFocus]);
  return {
    autoComplete: autoComplete,
    onBlur: autoComplete ? onBlur : noAutoCompleteOnBlur,
    onFocus: autoComplete ? onFocus : noAutoCompleteOnFocus
  };
};

var noop = function noop() {};

var useLayoutEffectInBrowser = typeof window === 'undefined' ? noop : useLayoutEffect;

/**
 * @returns The ref to be assigned to `ref` on the wrapping element of the input
 *
 * @param {Boolean} focus - The focus attribute of the parent component
 * @param {Object} [inputFocusRef] - The ref generated by react
 */

var useFocus = function useFocus(focus, inputFocusRef) {
  useLayoutEffectInBrowser(function () {
    var timeout;

    if (focus && inputFocusRef.current) {
      timeout = setTimeout(function () {
        inputFocusRef.current.focus();
        inputFocusRef.current.select();
      });
    }

    return function () {
      return timeout && clearTimeout(timeout);
    };
  }, [inputFocusRef, focus]);
};

var EMPTY_SELECT_VALUE = constants.EMPTY_SELECT_VALUE;
var EMPTY_OPTION = {
  value: EMPTY_SELECT_VALUE,
  label: 'Select one…'
};
var objectToKeyValueOptions = function objectToKeyValueOptions(object, includeEmptyOption) {
  if (includeEmptyOption === void 0) {
    includeEmptyOption = false;
  }

  return [].concat(_toConsumableArray(includeEmptyOption ? [EMPTY_OPTION] : []), _toConsumableArray(Object.entries(object || {}).map(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
        value = _ref2[0],
        label = _ref2[1];

    return {
      value: value,
      label: label
    };
  })));
};
var SELECT_OPTION_SHAPE = {
  value: PropTypes.any,
  label: PropTypes.node
};

var _DERIVED_FIELD_TYPE_F, _PLACEHOLDER_FOR_SCHE, _LEFT_ADORNMENT_FOR_S, _RIGHT_ADORNMENT_FOR_, _INPUT_MODE_FOR_SCHEM, _INPUT_MODE_FOR_SCHEM2, _PATTERN_FOR_SCHEMA_S, _AUTO_COMPLETE_FOR_SC, _FUNCTION_FOR_PROP, _PROPS_FOR_FIELD_TYPE, _Object$entries;
var SCHEMA_TYPES = types.SCHEMA_TYPES,
    SCHEMA_SUB_TYPES = types.SCHEMA_SUB_TYPES;
var FIELD_TYPES = {
  INPUT: 'INPUT',
  SELECT: 'SELECT',
  COMBOBOX: 'COMBOBOX',
  RADIO_GROUP: 'RADIO_GROUP',
  CHECKBOX: 'CHECKBOX',
  DATEPICKER: 'DATEPICKER',
  TEXTAREA: 'TEXTAREA',
  TOGGLE_BUTTON_GROUP: 'TOGGLE_BUTTON_GROUP',
  HIDDEN: 'HIDDEN'
};
var DERIVED_FIELD_TYPE_FOR_SCHEMA_TYPE = (_DERIVED_FIELD_TYPE_F = {}, _DERIVED_FIELD_TYPE_F[SCHEMA_TYPES.BOOLEAN] = FIELD_TYPES.CHECKBOX, _DERIVED_FIELD_TYPE_F[SCHEMA_TYPES.DATE] = FIELD_TYPES.DATEPICKER, _DERIVED_FIELD_TYPE_F[SCHEMA_TYPES.ARRAY] = FIELD_TYPES.COMBOBOX, _DERIVED_FIELD_TYPE_F);

var getDerivedFieldType = function getDerivedFieldType(schemaType, enumerable) {
  var _DERIVED_FIELD_TYPE_F2;

  return (_DERIVED_FIELD_TYPE_F2 = DERIVED_FIELD_TYPE_FOR_SCHEMA_TYPE[schemaType]) !== null && _DERIVED_FIELD_TYPE_F2 !== void 0 ? _DERIVED_FIELD_TYPE_F2 : enumerable ? FIELD_TYPES.SELECT : FIELD_TYPES.INPUT;
};

var PLACEHOLDER_FOR_SCHEMA_TYPE = (_PLACEHOLDER_FOR_SCHE = {}, _PLACEHOLDER_FOR_SCHE[SCHEMA_TYPES.DATE] = 'MM/DD/YYYY', _PLACEHOLDER_FOR_SCHE);

var getPlaceholderProp = function getPlaceholderProp(_ref) {
  var schemaType = _ref.schemaType;
  return PLACEHOLDER_FOR_SCHEMA_TYPE[schemaType];
};

var BOOLEAN_OPTIONS = {
  true: 'Yes',
  false: 'No'
};

var getOptionsProp = function getOptionsProp(_ref2, fieldType) {
  var schemaType = _ref2.schemaType,
      _ref2$enumeration = _ref2.enumeration;
  _ref2$enumeration = _ref2$enumeration === void 0 ? {} : _ref2$enumeration;
  var values = _ref2$enumeration.values,
      includeEmptyOption = _ref2$enumeration.includeEmptyOption;
  var options = schemaType === SCHEMA_TYPES.BOOLEAN ? BOOLEAN_OPTIONS : values;

  if ([FIELD_TYPES.COMBOBOX, FIELD_TYPES.SELECT].includes(fieldType)) {
    return objectToKeyValueOptions(options, includeEmptyOption);
  }

  return options;
};

var LEFT_ADORNMENT_FOR_SCHEMA_SUB_TYPE = (_LEFT_ADORNMENT_FOR_S = {}, _LEFT_ADORNMENT_FOR_S[SCHEMA_SUB_TYPES.NUMBER.DOLLAR] = '$', _LEFT_ADORNMENT_FOR_S);

var getLeftAdornmentProp = function getLeftAdornmentProp(_ref3) {
  var schemaSubType = _ref3.schemaSubType;
  return LEFT_ADORNMENT_FOR_SCHEMA_SUB_TYPE[schemaSubType];
};

var RIGHT_ADORNMENT_FOR_SCHEMA_SUB_TYPE = (_RIGHT_ADORNMENT_FOR_ = {}, _RIGHT_ADORNMENT_FOR_[SCHEMA_SUB_TYPES.STRING.SOCIAL_SECURITY_NUMBER] = /*#__PURE__*/jsx(IconLockOutline, {}), _RIGHT_ADORNMENT_FOR_);

var getRightAdornmentProp = function getRightAdornmentProp(_ref4) {
  var schemaSubType = _ref4.schemaSubType;
  return RIGHT_ADORNMENT_FOR_SCHEMA_SUB_TYPE[schemaSubType];
};

var SENSITIVE_SCHEMA_SUB_TYPES = new Set([SCHEMA_SUB_TYPES.STRING.SOCIAL_SECURITY_NUMBER]);

var getInputTypeProp = function getInputTypeProp(_ref5, fieldType) {
  var schemaSubType = _ref5.schemaSubType;

  if (fieldType === FIELD_TYPES.HIDDEN) {
    return 'hidden';
  }

  return SENSITIVE_SCHEMA_SUB_TYPES.has(schemaSubType) ? 'password' : undefined;
};

var INPUT_MODE_FOR_SCHEMA_TYPE = (_INPUT_MODE_FOR_SCHEM = {}, _INPUT_MODE_FOR_SCHEM[SCHEMA_TYPES.NUMBER] = 'numeric', _INPUT_MODE_FOR_SCHEM);
var INPUT_MODE_FOR_SCHEMA_SUB_TYPE = (_INPUT_MODE_FOR_SCHEM2 = {}, _INPUT_MODE_FOR_SCHEM2[SCHEMA_SUB_TYPES.STRING.SOCIAL_SECURITY_NUMBER] = 'numeric', _INPUT_MODE_FOR_SCHEM2[SCHEMA_SUB_TYPES.STRING.PHONE_NUMBER] = 'tel', _INPUT_MODE_FOR_SCHEM2[SCHEMA_SUB_TYPES.STRING.EMAIL] = 'email', _INPUT_MODE_FOR_SCHEM2[SCHEMA_SUB_TYPES.STRING.ADDRESS_CODE] = 'numeric', _INPUT_MODE_FOR_SCHEM2);

var getInputModeProp = function getInputModeProp(_ref6) {
  var _INPUT_MODE_FOR_SCHEM3;

  var schemaType = _ref6.schemaType,
      schemaSubType = _ref6.schemaSubType;
  return (_INPUT_MODE_FOR_SCHEM3 = INPUT_MODE_FOR_SCHEMA_SUB_TYPE[schemaSubType]) !== null && _INPUT_MODE_FOR_SCHEM3 !== void 0 ? _INPUT_MODE_FOR_SCHEM3 : INPUT_MODE_FOR_SCHEMA_TYPE[schemaType];
};

var PATTERN_FOR_SCHEMA_SUB_TYPE = (_PATTERN_FOR_SCHEMA_S = {}, _PATTERN_FOR_SCHEMA_S[SCHEMA_SUB_TYPES.NUMBER.DOLLAR] = '[0-9,.]*', _PATTERN_FOR_SCHEMA_S);

var getPatternProp = function getPatternProp(_ref7) {
  var schemaSubType = _ref7.schemaSubType;
  return PATTERN_FOR_SCHEMA_SUB_TYPE[schemaSubType];
};

var getLabelProp = function getLabelProp(_ref8) {
  var label = _ref8.label;
  return label;
};

var getRequiredProp = function getRequiredProp(_ref9) {
  var required = _ref9.required;
  return required;
};

var AUTO_COMPLETE_FOR_SCHEMA_SUB_TYPE = (_AUTO_COMPLETE_FOR_SC = {}, _AUTO_COMPLETE_FOR_SC[SCHEMA_SUB_TYPES.STRING.PHONE_NUMBER] = 'tel', _AUTO_COMPLETE_FOR_SC[SCHEMA_SUB_TYPES.STRING.EMAIL] = 'email', _AUTO_COMPLETE_FOR_SC[SCHEMA_SUB_TYPES.STRING.NAME_FIRST] = 'given-name', _AUTO_COMPLETE_FOR_SC[SCHEMA_SUB_TYPES.STRING.NAME_LAST] = 'family-name', _AUTO_COMPLETE_FOR_SC[SCHEMA_SUB_TYPES.STRING.ADDRESS_STREET] = 'address-line1', _AUTO_COMPLETE_FOR_SC[SCHEMA_SUB_TYPES.STRING.ADDRESS_UNIT] = 'address-line2', _AUTO_COMPLETE_FOR_SC[SCHEMA_SUB_TYPES.STRING.ADDRESS_CITY] = 'locality', _AUTO_COMPLETE_FOR_SC[SCHEMA_SUB_TYPES.STRING.ADDRESS_SUBDIVISION] = 'region', _AUTO_COMPLETE_FOR_SC[SCHEMA_SUB_TYPES.STRING.ADDRESS_CODE] = 'postal-code', _AUTO_COMPLETE_FOR_SC[SCHEMA_SUB_TYPES.STRING.ADDRESS_COUNTRY] = 'country', _AUTO_COMPLETE_FOR_SC);

var getAutoCompleteProp = function getAutoCompleteProp(_ref10) {
  var schemaSubType = _ref10.schemaSubType;
  return AUTO_COMPLETE_FOR_SCHEMA_SUB_TYPE[schemaSubType];
};

var PROPS = {
  LABEL: 'label',
  REQUIRED: 'required',
  OPTIONS: 'options',
  LEFT_ADORNMENT: 'leftAdornment',
  RIGHT_ADORNMENT: 'rightAdornment',
  PLACEHOLDER: 'placeholder',
  TYPE: 'type',
  INPUT_MODE: 'inputMode',
  PATTERN: 'pattern',
  AUTO_COMPLETE: 'autoComplete'
};
var FUNCTION_FOR_PROP = (_FUNCTION_FOR_PROP = {}, _FUNCTION_FOR_PROP[PROPS.LABEL] = getLabelProp, _FUNCTION_FOR_PROP[PROPS.REQUIRED] = getRequiredProp, _FUNCTION_FOR_PROP[PROPS.OPTIONS] = getOptionsProp, _FUNCTION_FOR_PROP[PROPS.LEFT_ADORNMENT] = getLeftAdornmentProp, _FUNCTION_FOR_PROP[PROPS.RIGHT_ADORNMENT] = getRightAdornmentProp, _FUNCTION_FOR_PROP[PROPS.PLACEHOLDER] = getPlaceholderProp, _FUNCTION_FOR_PROP[PROPS.TYPE] = getInputTypeProp, _FUNCTION_FOR_PROP[PROPS.INPUT_MODE] = getInputModeProp, _FUNCTION_FOR_PROP[PROPS.PATTERN] = getPatternProp, _FUNCTION_FOR_PROP[PROPS.AUTO_COMPLETE] = getAutoCompleteProp, _FUNCTION_FOR_PROP);
var INPUT_FIELD_PROPS = new Set([PROPS.LABEL, PROPS.REQUIRED, PROPS.LEFT_ADORNMENT, PROPS.RIGHT_ADORNMENT, PROPS.PLACEHOLDER, PROPS.INPUT_MODE, PROPS.PATTERN, PROPS.TYPE, PROPS.AUTO_COMPLETE]);
var PROPS_FOR_FIELD_TYPE = (_PROPS_FOR_FIELD_TYPE = {}, _PROPS_FOR_FIELD_TYPE[FIELD_TYPES.INPUT] = INPUT_FIELD_PROPS, _PROPS_FOR_FIELD_TYPE[FIELD_TYPES.SELECT] = new Set([PROPS.LABEL, PROPS.REQUIRED, PROPS.OPTIONS]), _PROPS_FOR_FIELD_TYPE[FIELD_TYPES.COMBOBOX] = new Set([PROPS.LABEL, PROPS.REQUIRED, PROPS.OPTIONS]), _PROPS_FOR_FIELD_TYPE[FIELD_TYPES.RADIO_GROUP] = new Set([PROPS.LABEL, PROPS.REQUIRED, PROPS.OPTIONS]), _PROPS_FOR_FIELD_TYPE[FIELD_TYPES.CHECKBOX] = new Set([PROPS.LABEL]), _PROPS_FOR_FIELD_TYPE[FIELD_TYPES.DATEPICKER] = new Set([PROPS.LABEL, PROPS.REQUIRED, PROPS.PLACEHOLDER]), _PROPS_FOR_FIELD_TYPE[FIELD_TYPES.TEXTAREA] = INPUT_FIELD_PROPS, _PROPS_FOR_FIELD_TYPE[FIELD_TYPES.TOGGLE_BUTTON_GROUP] = new Set([PROPS.LABEL, PROPS.REQUIRED, PROPS.OPTIONS]), _PROPS_FOR_FIELD_TYPE[FIELD_TYPES.HIDDEN] = new Set([PROPS.TYPE]), _PROPS_FOR_FIELD_TYPE);
var VALID_FIELD_TYPES_FOR_ALL_SCHEMAS = new Set([FIELD_TYPES.HIDDEN]);
var VALID_ENUMERABLE_FIELD_TYPES = new Set([].concat(_toConsumableArray(VALID_FIELD_TYPES_FOR_ALL_SCHEMAS), [FIELD_TYPES.RADIO_GROUP, FIELD_TYPES.SELECT, FIELD_TYPES.COMBOBOX, FIELD_TYPES.TOGGLE_BUTTON_GROUP]));
var VALID_FIELD_TYPES_FOR_SCHEMA_TYPE = Object.entries((_Object$entries = {}, _Object$entries[SCHEMA_TYPES.BOOLEAN] = new Set([FIELD_TYPES.CHECKBOX, FIELD_TYPES.RADIO_GROUP, FIELD_TYPES.SELECT, FIELD_TYPES.COMBOBOX, FIELD_TYPES.TOGGLE_BUTTON_GROUP]), _Object$entries[SCHEMA_TYPES.ARRAY] = new Set([FIELD_TYPES.COMBOBOX, FIELD_TYPES.INPUT]), _Object$entries[SCHEMA_TYPES.DATE] = new Set([FIELD_TYPES.DATEPICKER, FIELD_TYPES.INPUT]), _Object$entries[SCHEMA_TYPES.NUMBER] = new Set([FIELD_TYPES.INPUT]), _Object$entries[SCHEMA_TYPES.STRING] = new Set([FIELD_TYPES.INPUT, FIELD_TYPES.TEXTAREA].concat(_toConsumableArray(VALID_ENUMERABLE_FIELD_TYPES))), _Object$entries[SCHEMA_TYPES.MIXED] = new Set([FIELD_TYPES.INPUT, FIELD_TYPES.TEXTAREA].concat(_toConsumableArray(VALID_ENUMERABLE_FIELD_TYPES))), _Object$entries)).reduce(function (acc, _ref11) {
  var _Object$assign;

  var _ref12 = _slicedToArray(_ref11, 2),
      schema = _ref12[0],
      validFieldTypesForSchema = _ref12[1];

  return Object.assign({}, acc, (_Object$assign = {}, _Object$assign[schema] = new Set([].concat(_toConsumableArray(validFieldTypesForSchema), _toConsumableArray(VALID_FIELD_TYPES_FOR_ALL_SCHEMAS))), _Object$assign));
}, {});

var isValidOverrideFieldType = function isValidOverrideFieldType(overrideFieldType, schemaType, enumerable) {
  if (!overrideFieldType) {
    return false;
  }

  if (!FIELD_TYPES[overrideFieldType]) {
    // eslint-disable-next-line no-console
    console.warn(overrideFieldType + " is not a valid field type.");
    return false;
  }

  if (!VALID_FIELD_TYPES_FOR_SCHEMA_TYPE[schemaType].has(overrideFieldType)) {
    // eslint-disable-next-line no-console
    console.warn(overrideFieldType + " is not a valid field type for a schema of type " + schemaType + ".");
    return false;
  }

  if (enumerable && !VALID_ENUMERABLE_FIELD_TYPES.has(overrideFieldType)) {
    // eslint-disable-next-line no-console
    console.warn(overrideFieldType + " is not a valid field type for an enumerable schema.");
    return false;
  }

  return true;
};

var getFieldType = function getFieldType(overrideFieldType, schemaType, enumerable) {
  return isValidOverrideFieldType(overrideFieldType, schemaType, enumerable) ? overrideFieldType : getDerivedFieldType(schemaType, enumerable);
};

var getFieldTypeGenerator = function getFieldTypeGenerator(introspection) {
  return function (overrideFieldType) {
    var enumeration = introspection.enumeration,
        schemaType = introspection.schemaType;
    var enumerable = Boolean(enumeration);
    return getFieldType(overrideFieldType, schemaType, enumerable);
  };
};

var getFieldPropsGenerator = function getFieldPropsGenerator(introspection, getInputPropsForField) {
  return function (fieldType) {
    var enumeration = introspection.enumeration,
        name = introspection.name;
    var enumerable = Boolean(enumeration);
    var getInputProps = getInputPropsForField(fieldType);
    return _toConsumableArray(PROPS_FOR_FIELD_TYPE[fieldType]).reduce(function (acc, prop) {
      var _Object$assign2;

      return Object.assign({}, acc, (_Object$assign2 = {}, _Object$assign2[prop] = FUNCTION_FOR_PROP[prop](introspection, fieldType), _Object$assign2));
    }, getInputProps(name, enumerable));
  };
};

var getFieldsFromIntrospection = function getFieldsFromIntrospection(introspection, getInputPropsForField) {
  return Object.entries(introspection).reduce(function (acc, _ref13) {
    var _Object$assign3;

    var _ref14 = _slicedToArray(_ref13, 2),
        key = _ref14[0],
        value = _ref14[1];

    return value ? Object.assign({}, acc, (_Object$assign3 = {}, _Object$assign3[key] = value.schemaType ? {
      getFieldProps: getFieldPropsGenerator(value, getInputPropsForField),
      getFieldType: getFieldTypeGenerator(value)
    } : getFieldsFromIntrospection(value, getInputPropsForField), _Object$assign3)) : acc;
  }, {});
};

var getSchemaGeneratedFields = function getSchemaGeneratedFields(schema, values, getInputPropsForField) {
  var introspection = schema ? introspect.introspectSchema(schema, values) : {};
  return getFieldsFromIntrospection(introspection, getInputPropsForField);
};

var _excluded = ["mode", "criteriaMode", "validationSchema", "defaultValues", "readOnly"];
var EMPTY_SELECT_VALUE$1 = constants.EMPTY_SELECT_VALUE;
var formatSingleField = formatters.formatSingleField,
    deeplyFormat = formatters.deeplyFormat;
/**
 * Deeply removes empty string values from the argument object.
 * There are cases where an empty string is set as a default on register of an input, which can cause
 * requiredness validation errors for fields that have not been interacted with (as though the user left a required
 * field empty). In order to allow casting of the form that has been set, we strip empty values for the sake
 * of casting and let the form hook manage the validation errors that may exist.
 */

var removeEmptyStrings = function removeEmptyStrings(obj) {
  if (Array.isArray(obj)) {
    return obj.map(function (item) {
      return removeEmptyStrings(item);
    });
  }

  return Object.keys(obj || {}).reduce(function (prev, curr) {
    var _Object$assign2;

    var valueHere = obj[curr];
    var hasValues = valueHere !== '' && valueHere !== EMPTY_SELECT_VALUE$1;

    if (!hasValues) {
      return prev;
    }

    if (isPlainObject(valueHere)) {
      var _Object$assign;

      return Object.assign({}, prev, (_Object$assign = {}, _Object$assign[curr] = removeEmptyStrings(valueHere), _Object$assign));
    }

    return Object.assign({}, prev, (_Object$assign2 = {}, _Object$assign2[curr] = valueHere, _Object$assign2));
  }, {});
};
/**
 * Function to replace newly introduced errors and allow a successful cast to occur for other fields that are not in an error state.
 *
 * @param {object} errors - Errors generated by the form, should match the nested pattern of the form configuration.
 * @param {object} values - Form values to be casted, should be the nested values
 * @param {object} lastCastedFromValues - Last successful response from the schema cast, to be used to replace error fields with.
 */

var buildCastableValues = function buildCastableValues(errors, values, lastCastedFromValues) {
  return Object.entries(errors).filter(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
        value = _ref2[1];

    return value;
  }).reduce(function (memo, _ref3) {
    var _Object$assign4;

    var _ref4 = _slicedToArray(_ref3, 1),
        key = _ref4[0];

    var valueAtKey = values[key];
    var lastCastedFromValue = lastCastedFromValues[key];

    if (isPlainObject(valueAtKey)) {
      var _Object$assign3;

      var castableValues = buildCastableValues(errors[key], valueAtKey, lastCastedFromValue);
      return Object.assign({}, memo, (_Object$assign3 = {}, _Object$assign3[key] = castableValues, _Object$assign3));
    }

    return Object.assign({}, memo, (_Object$assign4 = {}, _Object$assign4[key] = lastCastedFromValue, _Object$assign4));
  }, Object.assign({}, values));
};

var stripRefs = function stripRefs(errors) {
  if (_typeof(errors) !== 'object') {
    return errors;
  }

  return Object.keys(errors).reduce(function (acc, key) {
    var _errors$key, _Object$assign6;

    var hasRef = (_errors$key = errors[key]) === null || _errors$key === void 0 ? void 0 : _errors$key.ref;

    if (hasRef) {
      var _Object$assign5;

      var withoutRef = Object.assign({}, errors[key], {
        ref: null
      });
      return Object.assign({}, acc, (_Object$assign5 = {}, _Object$assign5[key] = withoutRef, _Object$assign5));
    }

    return Object.assign({}, acc, (_Object$assign6 = {}, _Object$assign6[key] = stripRefs(errors[key]), _Object$assign6));
  }, {});
};

var getNextFormID = function () {
  var formID = 0;
  return function () {
    return "form-" + ++formID;
  };
}();
/**
 * @callback updateFields
 * @param {Object} values - Field values as RHF returns
 * @param {Object} errors - Errors generated by RHF via the schema
 */

/**
 * @param {Object} rhfConfiguration - React Hook Form configuration, defaults mode to onBlur and validateCriteriaMode to all
 * @param {Object} formConfiguration - Configuration
 * @param {Object} formConfiguration.formatters - [field name] : formatter returns formatted value
 * @param {updateFields} formConfiguration.updateFields - Callback to update fields when changes are evaluated
 */


var useForm = function useForm(_temp, _temp2) {
  var _castedValues$current11, _castedValues$current12;

  var _ref5 = _temp === void 0 ? {} : _temp,
      _ref5$mode = _ref5.mode,
      mode = _ref5$mode === void 0 ? 'onBlur' : _ref5$mode,
      _ref5$criteriaMode = _ref5.criteriaMode,
      criteriaMode = _ref5$criteriaMode === void 0 ? 'all' : _ref5$criteriaMode,
      validationSchema = _ref5.validationSchema,
      defaultValues = _ref5.defaultValues,
      readOnly = _ref5.readOnly,
      rhfConfiguration = _objectWithoutProperties(_ref5, _excluded);

  var _ref6 = _temp2 === void 0 ? {} : _temp2,
      inputFormatters = _ref6.formatters,
      updateFields = _ref6.updateFields,
      setCastedValues = _ref6.setCastedValues,
      valuesToPersist = _ref6.valuesToPersist,
      _ref6$resetOnSubmit = _ref6.resetOnSubmit,
      resetOnSubmit = _ref6$resetOnSubmit === void 0 ? true : _ref6$resetOnSubmit,
      _ref6$generateFormatt = _ref6.generateFormattersFromSchema,
      generateFormattersFromSchema = _ref6$generateFormatt === void 0 ? false : _ref6$generateFormatt;

  var castedValues = useRef();
  var generatedFormatters = useMemo(function () {
    return generateFormattersFromSchema ? introspect.getFieldFormattersFromSchema(validationSchema) : {};
  }, [generateFormattersFromSchema, validationSchema]);
  var formatters = useMemo(function () {
    return Object.assign({}, generatedFormatters, inputFormatters);
  }, [generatedFormatters, inputFormatters]); // To align with React Hook Form's behavior with regard to default values we only want to account for
  // default values on initial mount, any subsequent values should be pulled from form state

  var _useState = useState(deeplyFormat(defaultValues, formatters, false), []),
      _useState2 = _slicedToArray(_useState, 2),
      internalDefaultValues = _useState2[0],
      setInternalDefaultValues = _useState2[1];

  var formHookResult = useForm$1(Object.assign({
    mode: mode,
    criteriaMode: criteriaMode,
    resolver: validationSchema && yupResolver(validationSchema),
    defaultValues: internalDefaultValues
  }, rhfConfiguration));
  var rhfSetValue = formHookResult.setValue,
      rhfReset = formHookResult.reset,
      getValues = formHookResult.getValues,
      errors = formHookResult.errors,
      rhfRegister = formHookResult.register,
      trigger = formHookResult.trigger,
      formState = formHookResult.formState,
      clearErrors = formHookResult.clearErrors,
      control = formHookResult.control;

  var _useState3 = useState(true),
      _useState4 = _slicedToArray(_useState3, 2),
      isFirstRender = _useState4[0],
      setIsFirstRender = _useState4[1];

  var updateCastedFields = useCallback(function () {
    var currentFormValues = isFirstRender ? internalDefaultValues : getValues();

    try {
      if (validationSchema) {
        var valuesToCastRaw = castedValues.current ? buildCastableValues(errors, currentFormValues, castedValues.current.castedFrom) : currentFormValues;
        var valuesToCast = removeEmptyStrings(valuesToCastRaw);
        castedValues.current = {
          casted: validationSchema.cast(valuesToCast),
          castedFrom: valuesToCast,
          raw: valuesToCastRaw
        };
        setCastedValues === null || setCastedValues === void 0 ? void 0 : setCastedValues(castedValues.current.casted);
      } else {
        castedValues.current = {
          castedFrom: currentFormValues,
          raw: currentFormValues
        };
      }
    } catch (e) {
      var _castedValues$current;

      castedValues.current = {
        casted: (_castedValues$current = castedValues.current) === null || _castedValues$current === void 0 ? void 0 : _castedValues$current.casted,
        castedFrom: currentFormValues,
        raw: currentFormValues
      };
    }
  }, [castedValues, validationSchema, errors, setCastedValues, getValues, isFirstRender, internalDefaultValues]);
  var reset = useCallback(function (values) {
    var toResetWith = deeplyFormat(values, formatters, false); // When a reset is performed, we want to ensure that the defaultValue property
    // supplied to the input elements reflects the new defaults provided to the reset

    setInternalDefaultValues(toResetWith);
    setCastedValues === null || setCastedValues === void 0 ? void 0 : setCastedValues(values);
    castedValues.current = {
      casted: values,
      castedFrom: toResetWith,
      raw: values
    };

    for (var _len = arguments.length, rest = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      rest[_key - 1] = arguments[_key];
    }

    rhfReset.apply(void 0, [toResetWith].concat(rest));
  }, [rhfReset, formatters, setCastedValues]);
  var register = useCallback(function (refOrName) {
    var _castedValues$current2;

    for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
      args[_key2 - 1] = arguments[_key2];
    }

    rhfRegister.apply(void 0, [refOrName].concat(args));
    var name = typeof refOrName === 'string' ? refOrName : refOrName === null || refOrName === void 0 ? void 0 : refOrName.name;

    if (typeof name === 'string' && castedValues.current && !has((_castedValues$current2 = castedValues.current) === null || _castedValues$current2 === void 0 ? void 0 : _castedValues$current2.raw, name) && has(internalDefaultValues, name)) {
      updateCastedFields();
    }
  }, [rhfRegister, updateCastedFields, internalDefaultValues, castedValues]);
  var setValue = useCallback(function (key, valueOrValidate) {
    var $args = arguments;
    return new Promise(function ($return, $error) {
      var _castedValues$current5, _castedValues$current6, _castedValues$current7, _castedValues$current8, _castedValues$current9, _castedValues$current10, valueToSet, _castedValues$current3, _castedValues$current4, schemaForKey, castedValue, castedValuesClone, formatted, _formatted, currentFormValues, currentValue, _len3, rest, _key3, existingCastedValues, updatedCastedValues, errorsWithoutRefs;

      valueToSet = valueOrValidate;

      if (validationSchema) {
        var $Try_2_Post = function () {
          try {
            return $If_3.call(this);
          } catch ($boundEx) {
            return $error($boundEx);
          }
        }.bind(this);

        var $Try_2_Catch = function (err) {
          try {
            // we've failed validation, so use the raw un-casted/un-formatted value that came in
            valueToSet = valueOrValidate;
            return $Try_2_Post();
          } catch ($boundEx) {
            return $error($boundEx);
          }
        };

        try {
          schemaForKey = zgmYup.reach(validationSchema, key);
          castedValue = schemaForKey.cast(valueOrValidate);
          castedValuesClone = cloneDeep((_castedValues$current3 = (_castedValues$current4 = castedValues.current) === null || _castedValues$current4 === void 0 ? void 0 : _castedValues$current4.casted) !== null && _castedValues$current3 !== void 0 ? _castedValues$current3 : {});
          set(castedValuesClone, key, castedValue);
          return Promise.resolve(validationSchema.validateAt(key, castedValuesClone)).then(function ($await_4) {
            try {
              // at this point we know we've successfully passed validation and should therefor clear errors at this key
              clearErrors(key);
              formatted = formatSingleField(valueOrValidate, key, formatters);
              valueToSet = formatted;
              return $Try_2_Post();
            } catch ($boundEx) {
              return $Try_2_Catch($boundEx);
            }
          }, $Try_2_Catch);
        } catch (err) {
          $Try_2_Catch(err);
        }
      } else {
        _formatted = formatSingleField(valueOrValidate, key, formatters);
        // Naive validity check. Most formatters return null if it cannot be formatted.
        // If we do not check for null, it will remove the invalid value and, if not required, not error.
        valueToSet = _formatted === null ? valueToSet : _formatted;
        return $If_3.call(this);
      }

      function $If_3() {
        currentFormValues = getValues();
        currentValue = get(currentFormValues, key);

        if (valueToSet !== currentValue) {
          for (_len3 = $args.length, rest = new Array(_len3 > 2 ? _len3 - 2 : 0), _key3 = 2; _key3 < _len3; _key3++) {
            rest[_key3 - 2] = $args[_key3];
          }

          rhfSetValue.apply(void 0, [key, valueToSet].concat(rest));
        }

        existingCastedValues = cloneDeep((_castedValues$current5 = (_castedValues$current6 = castedValues.current) === null || _castedValues$current6 === void 0 ? void 0 : _castedValues$current6.casted) !== null && _castedValues$current5 !== void 0 ? _castedValues$current5 : (_castedValues$current7 = castedValues.current) === null || _castedValues$current7 === void 0 ? void 0 : _castedValues$current7.castedFrom);
        updateCastedFields();
        updatedCastedValues = (_castedValues$current8 = (_castedValues$current9 = castedValues.current) === null || _castedValues$current9 === void 0 ? void 0 : _castedValues$current9.casted) !== null && _castedValues$current8 !== void 0 ? _castedValues$current8 : (_castedValues$current10 = castedValues.current) === null || _castedValues$current10 === void 0 ? void 0 : _castedValues$current10.castedFrom;
        errorsWithoutRefs = stripRefs(errors);

        if (!isEqual(updatedCastedValues, existingCastedValues)) {
          updateFields === null || updateFields === void 0 ? void 0 : updateFields(updatedCastedValues, errorsWithoutRefs);
        }

        return $return();
      }
    });
  }, [updateFields, errors, updateCastedFields, rhfSetValue, clearErrors, getValues, formatters, validationSchema]); // formID is used to generate unique input `id` attributes

  var formIDRef = useRef(null);

  var getFormID = function getFormID() {
    var _formIDRef$current;

    return formIDRef.current = (_formIDRef$current = formIDRef.current) !== null && _formIDRef$current !== void 0 ? _formIDRef$current : getNextFormID();
  };

  var registerAndSetPersistedValues = useCallback(function (values) {
    valuesToPersist === null || valuesToPersist === void 0 ? void 0 : valuesToPersist.forEach(function (name) {
      rhfRegister({
        name: name
      });
      setValue(name, get(values, name));
    });
  }, [valuesToPersist, rhfRegister, setValue]);
  useEffect(function () {
    registerAndSetPersistedValues(internalDefaultValues); // Only register and set initial values to persist on mount

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);
  useEffect(function () {
    if (resetOnSubmit && formState.isSubmitSuccessful) {
      var values = getValues(); // reset the form state (isDirty, isValid, isSubmitted, etc) but maintain the existing values

      reset(values);
      registerAndSetPersistedValues(values);
    } // Only check if form should be reset when formState or resetOnSubmit changes

    /* eslint-disable-next-line react-hooks/exhaustive-deps */

  }, [formState, resetOnSubmit]);
  var handleFormFieldChange = useCallback(function (_ref7) {
    return new Promise(function ($return, $error) {
      var _ref7$target, name, value;

      _ref7$target = _ref7.target, name = _ref7$target.name, value = _ref7$target.value;
      return Promise.resolve(setValue(name, value)).then(function ($await_5) {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      }, $error);
    });
  }, [setValue]);
  var getBaseInputProps = useCallback(function (name) {
    return {
      id: getFormID() + "_" + name,
      name: name,
      register: register,
      error: get(errors, name)
    };
  }, [errors, register]);
  var getInputProps = useCallback(function (name, onChange) {
    var _Object$assign7;

    return Object.assign({}, getBaseInputProps(name), (_Object$assign7 = {}, _Object$assign7[onChange ? 'onChange' : 'onBlur'] = handleFormFieldChange, _Object$assign7.defaultValue = internalDefaultValues ? get(internalDefaultValues, name) : '', _Object$assign7.readOnly = readOnly, _Object$assign7));
  }, [getBaseInputProps, handleFormFieldChange, internalDefaultValues, readOnly]);
  var handleComboboxChange = useCallback(function (_ref8) {
    return new Promise(function ($return, $error) {
      var value, name;
      value = _ref8.value, name = _ref8.name;
      return Promise.resolve(setValue(name, value)).then(function ($await_6) {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      }, $error);
    });
  }, [setValue]);
  var getComboboxProps = useCallback(function (name, onChange) {
    var _Object$assign8;

    return Object.assign({}, getBaseInputProps(name), (_Object$assign8 = {}, _Object$assign8[onChange ? 'onChange' : 'onBlur'] = handleComboboxChange, _Object$assign8.defaultValue = internalDefaultValues ? get(internalDefaultValues, name) : [], _Object$assign8.readOnly = readOnly, _Object$assign8.control = control, _Object$assign8));
  }, [getBaseInputProps, handleComboboxChange, internalDefaultValues, readOnly, control]);
  var handleCheckboxChange = useCallback(function (_ref9) {
    return new Promise(function ($return, $error) {
      var _ref9$target, name, checked;

      _ref9$target = _ref9.target, name = _ref9$target.name, checked = _ref9$target.checked;
      return Promise.resolve(setValue(name, checked)).then(function ($await_7) {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      }, $error);
    });
  }, [setValue]);
  var getCheckboxInputProps = useCallback(function (name) {
    return Object.assign({}, getBaseInputProps(name), {
      onChange: handleCheckboxChange,
      defaultChecked: internalDefaultValues ? get(internalDefaultValues, name) : false,
      disabled: readOnly
    });
  }, [getBaseInputProps, handleCheckboxChange, internalDefaultValues, readOnly]);
  var getRadioInputProps = useCallback(function (name) {
    var _get;

    return Object.assign({}, getBaseInputProps(name), {
      onChange: handleFormFieldChange,
      value: (_get = get(internalDefaultValues, name)) !== null && _get !== void 0 ? _get : '',
      disabled: readOnly
    });
  }, [getBaseInputProps, handleFormFieldChange, internalDefaultValues, readOnly]);
  var handleDatePickerChange = useCallback(function (name, date) {
    return new Promise(function ($return, $error) {
      return Promise.resolve(setValue(name, date)).then(function ($await_8) {
        try {
          // selecting a date in the date picker does not trigger RHF to re-validate,
          // thus we manually trigger validation to clear any existing errors
          if (get(errors, name)) {
            trigger(name);
          }

          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      }, $error);
    });
  }, [setValue, errors, trigger]);
  var getDatePickerInputProps = useCallback(function (name) {
    return Object.assign({}, getBaseInputProps(name), {
      onDateChange: function onDateChange(date) {
        return new Promise(function ($return, $error) {
          return Promise.resolve(handleDatePickerChange(name, date)).then($return, $error);
        });
      },
      defaultValue: internalDefaultValues ? get(internalDefaultValues, name) : undefined,
      readOnly: readOnly
    });
  }, [getBaseInputProps, handleDatePickerChange, internalDefaultValues, readOnly]);
  var getToggleButtonGroupInputProps = useCallback(function (name) {
    return Object.assign({}, getBaseInputProps(name), {
      onChange: function onChange(value) {
        return setValue(name, value);
      },
      defaultValue: internalDefaultValues ? get(internalDefaultValues, name) : undefined,
      readOnly: readOnly
    });
  }, [getBaseInputProps, internalDefaultValues, readOnly, setValue]);
  var getInputPropsForField = useCallback(function (fieldType) {
    var _fieldTypeToGetInputP;

    var fieldTypeToGetInputProps = (_fieldTypeToGetInputP = {}, _fieldTypeToGetInputP[FIELD_TYPES.DATEPICKER] = getDatePickerInputProps, _fieldTypeToGetInputP[FIELD_TYPES.RADIO_GROUP] = getRadioInputProps, _fieldTypeToGetInputP[FIELD_TYPES.CHECKBOX] = getCheckboxInputProps, _fieldTypeToGetInputP[FIELD_TYPES.TOGGLE_BUTTON_GROUP] = getToggleButtonGroupInputProps, _fieldTypeToGetInputP[FIELD_TYPES.SELECT] = getInputProps, _fieldTypeToGetInputP[FIELD_TYPES.COMBOBOX] = getComboboxProps, _fieldTypeToGetInputP[FIELD_TYPES.INPUT] = getInputProps, _fieldTypeToGetInputP[FIELD_TYPES.TEXTAREA] = getInputProps, _fieldTypeToGetInputP[FIELD_TYPES.HIDDEN] = getInputProps, _fieldTypeToGetInputP);
    return fieldTypeToGetInputProps[fieldType];
  }, [getDatePickerInputProps, getCheckboxInputProps, getInputProps, getRadioInputProps, getToggleButtonGroupInputProps, getComboboxProps]);
  var valuesToGenerateFields = (_castedValues$current11 = castedValues === null || castedValues === void 0 ? void 0 : (_castedValues$current12 = castedValues.current) === null || _castedValues$current12 === void 0 ? void 0 : _castedValues$current12.casted) !== null && _castedValues$current11 !== void 0 ? _castedValues$current11 : internalDefaultValues;
  var fields = useMemo(function () {
    return getSchemaGeneratedFields(validationSchema, valuesToGenerateFields, getInputPropsForField);
  }, [validationSchema, valuesToGenerateFields, getInputPropsForField]);
  useEffect(function () {
    setIsFirstRender(false);
  }, []);
  return Object.assign({}, formHookResult, {
    register: register,
    reset: reset,
    setValue: setValue,
    getInputProps: getInputProps,
    getComboboxProps: getComboboxProps,
    getCheckboxInputProps: getCheckboxInputProps,
    getRadioInputProps: getRadioInputProps,
    getDatePickerInputProps: getDatePickerInputProps,
    getToggleButtonGroupInputProps: getToggleButtonGroupInputProps,
    useFieldArray: function useFieldArray$1(config) {
      return useFieldArray(Object.assign({}, config, {
        control: formHookResult.control
      }));
    },
    fields: fields
  });
};

/**
 * This is ignored from coverage because testing that PropTypes works makes no sense.
 */

/* istanbul ignore next */

var ERROR_PROP_SHAPE = PropTypes.shape({
  message: PropTypes.string
});

var _excluded$1 = ["InputComponent", "className", "error", "description", "renderLabel", "children", "required", "id"];

var LabeledInput = function LabeledInput(_ref) {
  var InputComponent = _ref.InputComponent,
      className = _ref.className,
      error = _ref.error,
      description = _ref.description,
      renderLabel = _ref.renderLabel,
      children = _ref.children,
      required = _ref.required,
      id = _ref.id,
      props = _objectWithoutProperties(_ref, _excluded$1);

  // ToggleButtonGroup / ButtonGroup is not considered a form element by Constellation and thus
  // doesn't have aria-labelledby wired up automatically by Constellation, so we explicitly
  // construct it and provide it to renderLabel / control.
  var labelID = useMemo(function () {
    return uniqueId('label_');
  }, []);
  return /*#__PURE__*/jsx(FormField, {
    className: className,
    required: required,
    controlId: id !== null && id !== void 0 ? id : props.name,
    label: renderLabel({
      id: labelID,
      children: children
    }),
    control: /*#__PURE__*/jsx(InputComponent, Object.assign({
      error: error,
      "aria-labelledby": labelID
    }, props)),
    description: /*#__PURE__*/jsxs(Fragment, {
      children: [description && /*#__PURE__*/jsx(FormHelp, {
        children: description
      }), error && /*#__PURE__*/jsx(FormHelp, {
        error: true,
        children: error.message
      })]
    })
  });
};

LabeledInput.SimpleLabel = function (props) {
  return /*#__PURE__*/jsx(Label, Object.assign({}, props));
};

LabeledInput.SimpleLabel.propTypes = {
  children: PropTypes.node.isRequired
};
LabeledInput.propTypes = {
  InputComponent: PropTypes.elementType,
  renderLabel: PropTypes.func,
  className: PropTypes.string,
  children: PropTypes.node,
  label: PropTypes.node,
  error: ERROR_PROP_SHAPE,
  description: PropTypes.node,
  required: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string
};
LabeledInput.defaultProps = {
  renderLabel: LabeledInput.SimpleLabel
};
var withLabel = function withLabel(InputComponent) {
  return function (props) {
    return /*#__PURE__*/jsx(LabeledInput, Object.assign({
      InputComponent: InputComponent
    }, props));
  };
};

var _templateObject, _templateObject2;
var inputReadOnly = css(_templateObject || (_templateObject = _taggedTemplateLiteralLoose(["\n    &&& {\n        background-color: transparent;\n        border-color: transparent;\n    }\n    &:focus {\n        box-shadow: none;\n    }\n"])));
var readOnlyMixin = function readOnlyMixin(assignOnReadOnly) {
  if (assignOnReadOnly === void 0) {
    assignOnReadOnly = inputReadOnly;
  }

  return function (_ref) {
    var readOnly = _ref.readOnly;
    return readOnly ? assignOnReadOnly : '';
  };
};
var ReadOnlyCapableAdornedInput = /*#__PURE__*/styled(AdornedInput).withConfig({
  componentId: "sc-15n0lxp-0"
})(readOnlyMixin(css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteralLoose(["\n        &&&&& {\n            ", ", ", " {\n                background-color: transparent;\n                border-color: transparent;\n            }\n        }\n        &:focus-within {\n            box-shadow: none;\n        }\n    "])), StyledInput, StyledAdornment)));

var _excluded$2 = ["className", "disabled", "focus", "error", "register", "readOnly", "leftAdornment", "rightAdornment"];

var RHFInput = function RHFInput(_ref) {
  var className = _ref.className,
      disabled = _ref.disabled,
      focus = _ref.focus,
      error = _ref.error,
      register = _ref.register,
      readOnly = _ref.readOnly,
      leftAdornment = _ref.leftAdornment,
      rightAdornment = _ref.rightAdornment,
      props = _objectWithoutProperties(_ref, _excluded$2);

  var inputFocusRef = useRef();
  useFocus(focus, inputFocusRef);
  return /*#__PURE__*/jsx(ReadOnlyCapableAdornedInput, {
    readOnly: readOnly,
    className: className,
    inputId: props.id || props.name,
    disabled: disabled,
    error: !!error,
    leftAdornment: leftAdornment && /*#__PURE__*/jsx(Adornment, {
      children: leftAdornment
    }),
    rightAdornment: rightAdornment && /*#__PURE__*/jsx(Adornment, {
      children: rightAdornment
    }),
    input: /*#__PURE__*/jsx(Input, Object.assign({}, props, useAutoComplete(inputFocusRef, props), {
      readOnly: readOnly,
      ref: function ref(e) {
        inputFocusRef.current = e;
        register(e);
      }
    }))
  });
};

RHFInput.propTypes = {
  readOnly: PropTypes.bool,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
  register: PropTypes.func,
  leftAdornment: PropTypes.node,
  rightAdornment: PropTypes.node,
  error: ERROR_PROP_SHAPE,
  focus: PropTypes.bool,
  autoComplete: PropTypes.string
};
RHFInput.Labeled = withLabel(RHFInput);

var _excluded$3 = ["readOnly", "options", "register", "focus", "value", "defaultValue", "onChange", "error", "onBlur"],
    _excluded2 = ["group"];

var MaybeOptGroup = function MaybeOptGroup(_ref) {
  var children = _ref.children,
      label = _ref.label;
  return label ? /*#__PURE__*/jsx("optgroup", {
    label: label,
    children: children
  }) : /*#__PURE__*/jsx(Fragment, {
    children: children
  });
};

MaybeOptGroup.propTypes = {
  children: PropTypes.node,
  label: PropTypes.string
};
var StyledSelect = /*#__PURE__*/styled(Select).withConfig({
  componentId: "sc-cb4ucf-0"
})(readOnlyMixin());

var noop$1 = function noop() {};

var RHFSelect = function RHFSelect(_ref2) {
  var readOnly = _ref2.readOnly,
      options = _ref2.options,
      register = _ref2.register,
      focus = _ref2.focus,
      value = _ref2.value,
      defaultValue = _ref2.defaultValue,
      onChange = _ref2.onChange,
      error = _ref2.error,
      onBlur = _ref2.onBlur,
      props = _objectWithoutProperties(_ref2, _excluded$3);

  var inputFocusRef = useRef();
  useFocus(focus, inputFocusRef);
  var currentSelectedValue = value !== null && value !== void 0 ? value : defaultValue; // By using a Map, we can group options that don't have a `.group`
  // with `undefined` as the key (instead of `"undefined"` with an object).
  // That allows `MaybeOptGroup` to do a truthy check on the group label
  // and skip rendering the `<optgroup>` on a group-by-group basis.
  // This also means grouped and non-grouped options are handled through a
  // single code path, and ensures that `EMPTY_OPTION` does not get grouped.

  var groupedOptions = useMemo(function () {
    return options.reduce(function (groups, _ref3) {
      var group = _ref3.group,
          option = _objectWithoutProperties(_ref3, _excluded2);

      if (!groups.has(group)) {
        groups.set(group, []);
      }

      var groupOptions = groups.get(group);
      groupOptions.push(option);
      return groups;
    }, new Map());
  }, [options]);

  var noopWhenReadOnly = function noopWhenReadOnly(onEvent) {
    // don't override a function that doesn't exist
    if (typeof onEvent === 'function') {
      return readOnly ? noop$1 : onEvent;
    }

    return onEvent;
  };

  return /*#__PURE__*/jsx(Fragment, {
    children: /*#__PURE__*/jsx(StyledSelect, Object.assign({
      ref: function ref(e) {
        inputFocusRef.current = e;
        register(e);
      },
      readOnly: readOnly,
      onChange: noopWhenReadOnly(onChange),
      onBlur: noopWhenReadOnly(onBlur),
      defaultValue: defaultValue,
      value: value,
      error: !!error
    }, props, {
      children: Array.from(groupedOptions, function (_ref4) {
        var _ref5 = _slicedToArray(_ref4, 2),
            group = _ref5[0],
            groupOptions = _ref5[1];

        return /*#__PURE__*/jsx(MaybeOptGroup, {
          label: group,
          children: groupOptions.map(function (_ref6) {
            var optionValue = _ref6.value,
                label = _ref6.label;
            return /*#__PURE__*/jsx("option", {
              value: optionValue,
              disabled: readOnly && currentSelectedValue !== optionValue,
              children: label
            }, optionValue);
          })
        }, group !== null && group !== void 0 ? group : 'no-group');
      })
    }))
  });
};

RHFSelect.propTypes = {
  readOnly: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  value: PropTypes.any,
  error: ERROR_PROP_SHAPE,
  defaultValue: PropTypes.any,
  options: PropTypes.arrayOf(PropTypes.shape(SELECT_OPTION_SHAPE)).isRequired,
  register: PropTypes.func,
  name: PropTypes.string,
  focus: PropTypes.bool
};
RHFSelect.EMPTY_OPTION = EMPTY_OPTION;
RHFSelect.Labeled = withLabel(RHFSelect);

var STATE_CODES = constants.STATE_CODES;
var StyledSameAddressWrapper = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-1xcwxfu-0"
})(["", "{line-height:21px;}margin-left:", ";"], StyledLabel, spaceMixin('sm'));
var StyledAddressName = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-1xcwxfu-1"
})(["display:flex;align-items:center;justify-content:space-between;"]);
var StyledAddressRow = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-1xcwxfu-2"
})(["display:flex;", ""], function (_ref) {
  var full = _ref.full;
  return "\n        &&& {\n            " + StyledFormField + " {\n                flex-basis: " + (full ? '100%' : '50%') + ";\n            }\n        }\n    ";
});
var StyledAddress = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-1xcwxfu-3"
})(["", " + ", "{margin-top:", ";}"], StyledAddressRow, StyledAddressRow, spaceMixin('xs'));

var Address = function Address(_ref2) {
  var addressName = _ref2.addressName,
      idBase = _ref2.idBase,
      getInputProps = _ref2.getInputProps,
      isSameInput = _ref2.isSameInput,
      isSameValue = _ref2.isSameValue,
      registerAt = _ref2.registerAt;
  return /*#__PURE__*/jsxs(StyledAddress, {
    children: [/*#__PURE__*/jsx(StyledAddressRow, {
      firstSectionRow: true,
      full: true,
      children: /*#__PURE__*/jsx(RHFInput.Labeled, Object.assign({}, getInputProps(registerAt + ".street"), {
        id: idBase + ".street",
        readOnly: isSameValue,
        children: /*#__PURE__*/jsxs(StyledAddressName, {
          children: [addressName, isSameInput && /*#__PURE__*/jsx(StyledSameAddressWrapper, {
            children: isSameInput
          })]
        })
      }))
    }), /*#__PURE__*/jsx(StyledAddressRow, {
      full: true,
      children: /*#__PURE__*/jsx(RHFInput.Labeled, Object.assign({
        single: true
      }, getInputProps(registerAt + ".city"), {
        id: idBase + ".city",
        readOnly: isSameValue,
        children: "City"
      }))
    }), /*#__PURE__*/jsxs(StyledAddressRow, {
      children: [/*#__PURE__*/jsx(RHFSelect.Labeled, Object.assign({}, getInputProps(registerAt + ".subdivision", true), {
        id: idBase + ".subdivision",
        options: objectToKeyValueOptions(STATE_CODES, true),
        readOnly: isSameValue,
        children: "State"
      })), /*#__PURE__*/jsx(RHFInput.Labeled, Object.assign({}, getInputProps(registerAt + ".code"), {
        id: idBase + ".code",
        readOnly: isSameValue,
        children: "ZIP Code"
      }))]
    })]
  });
};

Address.propTypes = {
  getInputProps: PropTypes.func.isRequired,
  addressName: PropTypes.string.isRequired,
  isCoBorrower: PropTypes.bool,
  fields: PropTypes.shape({}),
  idBase: PropTypes.string.isRequired,
  isSameInput: PropTypes.node,
  isSameValue: PropTypes.bool,
  registerAt: PropTypes.string
};
Address.Styled = StyledAddress;

var _excluded$4 = ["register", "children", "className", "focus", "error", "disabled", "defaultChecked"];

var RHFCheckbox = function RHFCheckbox(_ref) {
  var register = _ref.register,
      children = _ref.children,
      className = _ref.className,
      focus = _ref.focus,
      error = _ref.error,
      disabled = _ref.disabled,
      defaultChecked = _ref.defaultChecked,
      props = _objectWithoutProperties(_ref, _excluded$4);

  var inputFocusRef = useRef();
  var sanityCheckedDefaultChecked = typeof defaultChecked !== 'undefined' && JSON.parse(defaultChecked);
  useFocus(focus, inputFocusRef);
  var label = Children.count(children) > 0 ? children : props.label;
  return /*#__PURE__*/jsx(LabeledControl, {
    controlId: props.id || props.name,
    className: className,
    disabled: disabled,
    error: !!error,
    control: /*#__PURE__*/jsx(Checkbox, Object.assign({
      defaultChecked: sanityCheckedDefaultChecked
    }, props, {
      ref: function ref(e) {
        inputFocusRef.current = e;
        register(e);
      }
    })),
    label: /*#__PURE__*/jsx(Label, {
      children: label
    })
  });
};

RHFCheckbox.propTypes = {
  focus: PropTypes.bool,
  register: PropTypes.func,
  id: PropTypes.string,
  children: PropTypes.node,
  defaultChecked: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  name: PropTypes.string,
  label: PropTypes.string,
  className: PropTypes.string,
  error: PropTypes.shape({
    message: PropTypes.string
  }),
  disabled: PropTypes.bool
};

function requiredArgs(required, args) {
  if (args.length < required) {
    throw new TypeError(required + ' argument' + (required > 1 ? 's' : '') + ' required, but only ' + args.length + ' present');
  }
}

/**
 * @name toDate
 * @category Common Helpers
 * @summary Convert the given argument to an instance of Date.
 *
 * @description
 * Convert the given argument to an instance of Date.
 *
 * If the argument is an instance of Date, the function returns its clone.
 *
 * If the argument is a number, it is treated as a timestamp.
 *
 * If the argument is none of the above, the function returns Invalid Date.
 *
 * **Note**: *all* Date arguments passed to any *date-fns* function is processed by `toDate`.
 *
 * @param {Date|Number} argument - the value to convert
 * @returns {Date} the parsed date in the local time zone
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // Clone the date:
 * const result = toDate(new Date(2014, 1, 11, 11, 30, 30))
 * //=> Tue Feb 11 2014 11:30:30
 *
 * @example
 * // Convert the timestamp to date:
 * const result = toDate(1392098430000)
 * //=> Tue Feb 11 2014 11:30:30
 */

function toDate(argument) {
  requiredArgs(1, arguments);
  var argStr = Object.prototype.toString.call(argument); // Clone the date

  if (argument instanceof Date || typeof argument === 'object' && argStr === '[object Date]') {
    // Prevent the date to lose the milliseconds when passed to new Date() in IE10
    return new Date(argument.getTime());
  } else if (typeof argument === 'number' || argStr === '[object Number]') {
    return new Date(argument);
  } else {
    if ((typeof argument === 'string' || argStr === '[object String]') && typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn("Starting with v2.0.0-beta.1 date-fns doesn't accept strings as date arguments. Please use `parseISO` to parse strings. See: https://git.io/fjule"); // eslint-disable-next-line no-console

      console.warn(new Error().stack);
    }

    return new Date(NaN);
  }
}

/**
 * @name startOfDay
 * @category Day Helpers
 * @summary Return the start of a day for the given date.
 *
 * @description
 * Return the start of a day for the given date.
 * The result will be in the local timezone.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the original date
 * @returns {Date} the start of a day
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // The start of a day for 2 September 2014 11:55:00:
 * const result = startOfDay(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Tue Sep 02 2014 00:00:00
 */

function startOfDay(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  date.setHours(0, 0, 0, 0);
  return date;
}

/**
 * @name isSameDay
 * @category Day Helpers
 * @summary Are the given dates in the same day?
 *
 * @description
 * Are the given dates in the same day?
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} dateLeft - the first date to check
 * @param {Date|Number} dateRight - the second date to check
 * @returns {Boolean} the dates are in the same day
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Are 4 September 06:00:00 and 4 September 18:00:00 in the same day?
 * var result = isSameDay(new Date(2014, 8, 4, 6, 0), new Date(2014, 8, 4, 18, 0))
 * //=> true
 */

function isSameDay(dirtyDateLeft, dirtyDateRight) {
  requiredArgs(2, arguments);
  var dateLeftStartOfDay = startOfDay(dirtyDateLeft);
  var dateRightStartOfDay = startOfDay(dirtyDateRight);
  return dateLeftStartOfDay.getTime() === dateRightStartOfDay.getTime();
}

var _excluded$5 = ["className", "disabled", "readOnly", "focus", "error", "register", "minDate", "maxDate", "defaultValue", "onDateChange", "disabledDates"];
var date = formatters.inputFormatters.date;
var ONE_HUNDRED_YEARS_IN_MILLISECONDS = 1000 * 60 * 60 * 24 * 365 * 100;

var getDateOneHundredYearsAgo = function getDateOneHundredYearsAgo() {
  return new Date(new Date().getTime() - ONE_HUNDRED_YEARS_IN_MILLISECONDS);
};

var getDateOneHundredYearsInTheFuture = function getDateOneHundredYearsInTheFuture() {
  return new Date(new Date().getTime() + ONE_HUNDRED_YEARS_IN_MILLISECONDS);
};

var RHFDatePicker = function RHFDatePicker(_ref) {
  var className = _ref.className,
      disabled = _ref.disabled,
      readOnly = _ref.readOnly,
      focus = _ref.focus,
      error = _ref.error,
      register = _ref.register,
      minDate = _ref.minDate,
      maxDate = _ref.maxDate,
      defaultValue = _ref.defaultValue,
      onDateChange = _ref.onDateChange,
      disabledDates = _ref.disabledDates,
      datePickerProps = _objectWithoutProperties(_ref, _excluded$5);

  var propProvidedValue = datePickerProps.value;

  var _useState = useState(defaultValue !== null && defaultValue !== void 0 ? defaultValue : propProvidedValue),
      _useState2 = _slicedToArray(_useState, 2),
      value = _useState2[0],
      setValue = _useState2[1];

  useEffect(function () {
    setValue(propProvidedValue);
  }, [propProvidedValue]); // uncontrolled components will respect changes to `defaultValue` up until the user interacts
  // with the component and sets a value, so we simulate that here

  useEffect(function () {
    if (typeof value === 'undefined') {
      setValue(defaultValue);
    }
  }, [value, defaultValue]);
  var inputRef = useRef();
  useFocus(focus, inputRef); // the DatePickerInput component does not integrate well with the callback ref pattern we use
  // on other input components, thus we register the ref and directly pass it to the component

  useEffect(function () {
    register(inputRef.current);
  }, [register]);
  var isDatePickerPopupExpanded = useCallback(function () {
    var _inputRef$current;

    return (inputRef === null || inputRef === void 0 ? void 0 : (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.getAttribute('aria-expanded')) === 'true';
  }, []);

  var handleChange = function handleChange(inputValue, _ref2) {
    var reason = _ref2.reason;
    setValue(inputValue); // do not invoke onDateChange when manually typing an input; will be triggered via onBlur

    if (reason !== DATE_PICKER_CHANGE_REASON.MANUAL_INPUT) {
      onDateChange(inputValue);
    }
  }; // uncontrolled DatePickerInput components hide their state inside the component, which
  // blocks RHF from formatting the input value; thus, we implement a controlled component


  var setFormattedValue = useCallback(function (inputValue) {
    var formattedValue = date(inputValue);

    if (formattedValue && formattedValue !== inputValue) {
      setValue(formattedValue);
    }
  }, []);
  var handleBlur = useCallback(function (evt) {
    // onBlur events are triggered when the date picker popup appears, thus we stop that
    // event from bubbling up to prevent premature validation by RHF (validates onBlur)
    if (isDatePickerPopupExpanded()) {
      evt.stopPropagation();
    } else {
      var _evt$target;

      var inputValue = evt === null || evt === void 0 ? void 0 : (_evt$target = evt.target) === null || _evt$target === void 0 ? void 0 : _evt$target.value;
      onDateChange(inputValue);
      setFormattedValue(inputValue);
    }
  }, [isDatePickerPopupExpanded, onDateChange, setFormattedValue]);
  return /*#__PURE__*/jsx(ReadOnlyCapableAdornedInput, {
    className: className,
    inputId: datePickerProps.id || datePickerProps.name,
    disabled: disabled,
    error: !!error,
    readOnly: readOnly,
    rightAdornment: /*#__PURE__*/jsx(Adornment, {
      children: /*#__PURE__*/jsx(IconCalendarOutline, {})
    }),
    input: /*#__PURE__*/jsx(DatePickerInput, Object.assign({}, useAutoComplete(inputRef, datePickerProps), datePickerProps, {
      ref: inputRef,
      placeholder: "MM/DD/YYYY",
      onChange: handleChange,
      onBlur: handleBlur,
      value: value,
      readOnly: readOnly,
      datepicker: /*#__PURE__*/jsx(DatePicker, {
        minDate: minDate,
        maxDate: maxDate,
        selected: value,
        dateFormat: "M/d/yyyy",
        disabledDates: readOnly ? function (option) {
          return !isSameDay(new Date(option), new Date(value));
        } : disabledDates
      })
    }))
  });
};

RHFDatePicker.propTypes = {
  className: PropTypes.string,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
  register: PropTypes.func,
  error: ERROR_PROP_SHAPE,
  focus: PropTypes.bool,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  onDateChange: PropTypes.func,
  disabledDates: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PropTypes.instanceOf(Date))])
};
RHFDatePicker.defaultProps = {
  minDate: getDateOneHundredYearsAgo(),
  maxDate: getDateOneHundredYearsInTheFuture()
};
RHFDatePicker.Labeled = withLabel(RHFDatePicker);

var _excluded$6 = ["error"],
    _excluded2$1 = ["error"],
    _excluded3 = ["tabIndex", "homePrice", "getInputProps", "setValue", "downPayment", "downPaymentPercent", "percentDecimals"],
    _excluded4 = ["downPaymentPercentLabel", "errors", "DownPaymentComponent", "downPaymentId", "downPaymentPercentId", "renderAmountLabel"];
var StyledDownPaymentAmount = /*#__PURE__*/styled(RHFInput).withConfig({
  componentId: "sc-2qathf-0"
})(["", "{border-top-right-radius:0;border-bottom-right-radius:0;border-right-width:0;}flex-grow:1;flex-shrink:1;"], StyledInput);
var StyledDownPaymentPercent = /*#__PURE__*/styled(RHFInput).withConfig({
  componentId: "sc-2qathf-1"
})(["", "{border-top-left-radius:0;border-bottom-left-radius:0;}flex-shrink:0;", ""], StyledInput, function (_ref) {
  var percentDecimals = _ref.percentDecimals;
  return "\n        // 2 left digits, % adornment, 32px adornment padding + percent decimals in em\n        max-width: calc(" + (percentDecimals + 3) + "em + 32px);\n    ";
});
var DOWN_PAYMENT_PERCENT_LABEL_ID = 'downPaymentPercentLabel';

var Uncontrolled = function Uncontrolled(_ref2) {
  var tabIndex = _ref2.tabIndex,
      percentDecimals = _ref2.percentDecimals,
      _ref2$amountProps = _ref2.amountProps,
      amountError = _ref2$amountProps.error,
      amountProps = _objectWithoutProperties(_ref2$amountProps, _excluded$6),
      _ref2$percentProps = _ref2.percentProps,
      percentError = _ref2$percentProps.error,
      percentProps = _objectWithoutProperties(_ref2$percentProps, _excluded2$1);

  return /*#__PURE__*/jsxs(Flex, {
    display: "flex",
    children: [/*#__PURE__*/jsx(StyledDownPaymentAmount, Object.assign({
      tabIndex: tabIndex,
      inputMode: "numeric",
      pattern: "[0-9,.]*",
      leftAdornment: "$"
    }, amountProps, {
      error: amountError
    })), /*#__PURE__*/jsx(StyledDownPaymentPercent, Object.assign({
      percentDecimals: percentDecimals,
      tabIndex: tabIndex,
      inputMode: "decimal",
      pattern: "[0-9,.]*",
      rightAdornment: "%"
    }, percentProps, {
      error: percentError
    }))]
  });
};

Uncontrolled.propTypes = {
  tabIndex: PropTypes.string,
  percentDecimals: PropTypes.number,
  amountProps: PropTypes.shape({
    id: PropTypes.string.isRequired,
    error: PropTypes.object
  }).isRequired,
  percentProps: PropTypes.shape({
    id: PropTypes.string.isRequired,
    error: PropTypes.object
  })
};

var getPercentFromAmount = function getPercentFromAmount(amount, homePrice) {
  return parsers.int(amount) / homePrice * 100 || 0;
};

var getAmountFromPercent = function getAmountFromPercent(percent, homePrice) {
  return Math.round(parsers.float(percent) / 100 * homePrice) || 0;
};

var RHFDownPayment = function RHFDownPayment(_ref3) {
  var tabIndex = _ref3.tabIndex,
      homePrice = _ref3.homePrice,
      getInputProps = _ref3.getInputProps,
      setValue = _ref3.setValue,
      downPayment = _ref3.downPayment,
      downPaymentPercent = _ref3.downPaymentPercent,
      percentDecimals = _ref3.percentDecimals,
      props = _objectWithoutProperties(_ref3, _excluded3);

  var _useState = useState('percent'),
      _useState2 = _slicedToArray(_useState, 2),
      lastEdited = _useState2[0],
      setLastEdited = _useState2[1];

  useEffect(function () {
    var proposedAmount = getAmountFromPercent(downPaymentPercent, homePrice);
    var proposedPercentage = getPercentFromAmount(downPayment, homePrice);

    if (lastEdited === 'percent' && proposedAmount !== downPayment) {
      setValue('downPayment', proposedAmount);
    } else if (lastEdited === 'amount' && downPaymentPercent !== proposedPercentage) {
      setValue('downPaymentPercent', proposedPercentage);
    }
  }, [downPaymentPercent, downPayment, homePrice, lastEdited, setValue]);
  return /*#__PURE__*/jsx(Uncontrolled, {
    tabIndex: tabIndex,
    percentDecimals: percentDecimals,
    amountProps: Object.assign({}, props, getInputProps('downPayment'), {
      onChange: function onChange() {
        return setLastEdited('amount');
      }
    }),
    percentProps: Object.assign({}, props, getInputProps('downPaymentPercent'), {
      onChange: function onChange() {
        return setLastEdited('percent');
      },
      'aria-labelledby': DOWN_PAYMENT_PERCENT_LABEL_ID
    })
  });
};

RHFDownPayment.propTypes = {
  getInputProps: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  downPayment: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  downPaymentPercent: PropTypes.number.isRequired,
  homePrice: PropTypes.number.isRequired,
  tabIndex: PropTypes.string,
  percentDecimals: PropTypes.number
};
RHFDownPayment.defaultProps = {
  percentDecimals: 2
};
RHFDownPayment.Uncontrolled = Uncontrolled;

var LabeledRHFDownPayment = function LabeledRHFDownPayment(_ref4) {
  var _errors$downPayment;

  var downPaymentPercentLabel = _ref4.downPaymentPercentLabel,
      errors = _ref4.errors,
      DownPaymentComponent = _ref4.DownPaymentComponent,
      downPaymentId = _ref4.downPaymentId,
      downPaymentPercentId = _ref4.downPaymentPercentId,
      renderAmountLabel = _ref4.renderAmountLabel,
      props = _objectWithoutProperties(_ref4, _excluded4);

  return /*#__PURE__*/jsx(LabeledInput, Object.assign({
    InputComponent: DownPaymentComponent,
    renderLabel: function renderLabel(amountLabelProps) {
      return /*#__PURE__*/jsxs(Fragment, {
        children: [renderAmountLabel(amountLabelProps), /*#__PURE__*/jsx(VisuallyHidden, {
          children: /*#__PURE__*/jsx(Label, {
            id: DOWN_PAYMENT_PERCENT_LABEL_ID,
            htmlFor: downPaymentPercentId,
            children: downPaymentPercentLabel
          })
        })]
      });
    },
    error: (_errors$downPayment = errors === null || errors === void 0 ? void 0 : errors.downPayment) !== null && _errors$downPayment !== void 0 ? _errors$downPayment : errors === null || errors === void 0 ? void 0 : errors.downPaymentPercent,
    id: downPaymentId
  }, props));
};

LabeledRHFDownPayment.propTypes = {
  renderAmountLabel: PropTypes.func,
  downPaymentPercentLabel: PropTypes.node.isRequired,
  errors: PropTypes.shape({
    downPayment: PropTypes.shape({
      message: PropTypes.string.isRequired
    }),
    downPaymentPercent: PropTypes.shape({
      message: PropTypes.string.isRequired
    })
  }),
  DownPaymentComponent: PropTypes.elementType,
  downPaymentId: PropTypes.string.isRequired,
  downPaymentPercentId: PropTypes.string.isRequired
};
LabeledRHFDownPayment.defaultProps = {
  renderAmountLabel: LabeledInput.SimpleLabel
};

RHFDownPayment.LabeledUncontrolled = function (props) {
  return /*#__PURE__*/jsx(LabeledRHFDownPayment, Object.assign({
    DownPaymentComponent: Uncontrolled,
    downPaymentId: props.amountProps.id,
    downPaymentPercentId: props.percentProps.id
  }, props));
};

RHFDownPayment.LabeledUncontrolled.propTypes = {
  amountProps: PropTypes.shape({
    id: PropTypes.string.isRequired
  }).isRequired,
  percentProps: PropTypes.shape({
    id: PropTypes.string.isRequired
  }).isRequired
};

RHFDownPayment.Labeled = function (props) {
  return /*#__PURE__*/jsx(LabeledRHFDownPayment, Object.assign({
    DownPaymentComponent: RHFDownPayment,
    downPaymentId: props.getInputProps('downPayment').id,
    downPaymentPercentId: props.getInputProps('downPaymentPercent').id
  }, props));
};

RHFDownPayment.Labeled.propTypes = {
  getInputProps: PropTypes.func.isRequired
};

var _templateObject$1, _templateObject2$1;
var STRING_TRUE = constants.STRING_TRUE,
    STRING_FALSE = constants.STRING_FALSE;
var StyledContainer = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-1qpc3f7-0"
})(["display:flex;", ""], function (_ref) {
  var horizontalLayout = _ref.horizontalLayout;
  return css(_templateObject$1 || (_templateObject$1 = _taggedTemplateLiteralLoose(["\n        flex-direction: ", ";\n    "])), horizontalLayout ? 'row' : 'column');
});
var StyledLabeledControl = /*#__PURE__*/styled(LabeledControl).withConfig({
  componentId: "sc-1qpc3f7-1"
})(["flex-basis:50%;", ""], function (_ref2) {
  var horizontalLayout = _ref2.horizontalLayout;
  return css(_templateObject2$1 || (_templateObject2$1 = _taggedTemplateLiteralLoose(["\n        display: ", ";\n    "])), horizontalLayout ? 'inline-flex' : 'flex');
});

var RHFRadioGroup = function RHFRadioGroup(_ref3) {
  var register = _ref3.register,
      value = _ref3.value,
      options = _ref3.options,
      optionsSubsection = _ref3.optionsSubsection,
      name = _ref3.name,
      label = _ref3.label,
      required = _ref3.required,
      onChange = _ref3.onChange,
      error = _ref3.error,
      horizontalLayout = _ref3.horizontalLayout,
      disabled = _ref3.disabled;
  return /*#__PURE__*/jsxs(FormField, {
    children: [label && /*#__PURE__*/jsx(Label, {
      required: required,
      children: label
    }), /*#__PURE__*/jsx(StyledContainer, {
      horizontalLayout: horizontalLayout,
      children: Object.keys(options).map(function (optionValue) {
        return /*#__PURE__*/jsxs(Fragment$1, {
          children: [/*#__PURE__*/jsx(StyledLabeledControl, {
            horizontalLayout: horizontalLayout,
            id: name + "-" + optionValue,
            disabled: disabled,
            control: /*#__PURE__*/jsx(Radio, {
              name: name,
              value: optionValue,
              defaultChecked: value === optionValue,
              onChange: onChange,
              ref: register
            }),
            label: /*#__PURE__*/jsx(Label, {
              children: options[optionValue]
            })
          }), optionsSubsection === null || optionsSubsection === void 0 ? void 0 : optionsSubsection[optionValue]]
        }, optionValue);
      })
    }), error && /*#__PURE__*/jsx(FormHelp, {
      error: true,
      children: error.message
    })]
  });
};

RHFRadioGroup.propTypes = {
  register: PropTypes.func,
  value: PropTypes.string,
  options: PropTypes.object,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  required: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.shape({
    message: PropTypes.string
  }),
  horizontalLayout: PropTypes.bool,
  disabled: PropTypes.bool,
  optionsSubsection: PropTypes.objectOf(PropTypes.node)
};
var RHFBooleanRadioGroup = function RHFBooleanRadioGroup(props) {
  var _ref4;

  return /*#__PURE__*/jsx(RHFRadioGroup, Object.assign({}, props, {
    options: (_ref4 = {}, _ref4[STRING_TRUE] = 'Yes', _ref4[STRING_FALSE] = 'No', _ref4)
  }));
};

var _excluded$7 = ["onFocus", "onBlur", "revealable"];

var RHFPassword = function RHFPassword(_ref) {
  var onFocus = _ref.onFocus,
      onBlur = _ref.onBlur,
      revealable = _ref.revealable,
      props = _objectWithoutProperties(_ref, _excluded$7);

  var _useState = useState(undefined),
      _useState2 = _slicedToArray(_useState, 2),
      focused = _useState2[0],
      setFocused = _useState2[1];

  var handleFocus = useCallback(function () {
    setFocused(true);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return onFocus === null || onFocus === void 0 ? void 0 : onFocus.apply(void 0, args);
  }, [onFocus]);
  var handleBlur = useCallback(function () {
    setFocused(false);

    for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }

    return onBlur === null || onBlur === void 0 ? void 0 : onBlur.apply(void 0, args);
  }, [onBlur]);
  return /*#__PURE__*/jsx(RHFInput, Object.assign({}, props, {
    onFocus: revealable ? handleFocus : onFocus,
    onBlur: revealable ? handleBlur : onBlur,
    type: revealable && (focused || props.error) ? 'input' : props.type
  }));
};

RHFPassword.propTypes = {
  revealable: PropTypes.bool,
  error: PropTypes.bool,
  type: PropTypes.string,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func
};
RHFPassword.defaultProps = {
  revealable: false,
  type: 'password'
};
RHFPassword.Labeled = withLabel(RHFPassword);

var _excluded$8 = ["register", "focus", "error"];
var StyledTextarea = /*#__PURE__*/styled(Textarea).withConfig({
  componentId: "sc-17i6lsl-0"
})(readOnlyMixin());

var RHFTextarea = function RHFTextarea(_ref) {
  var register = _ref.register,
      focus = _ref.focus,
      error = _ref.error,
      props = _objectWithoutProperties(_ref, _excluded$8);

  var inputFocusRef = useRef();
  useFocus(focus, inputFocusRef);
  return /*#__PURE__*/jsx(StyledTextarea, Object.assign({
    error: !!error
  }, props, useAutoComplete(inputFocusRef, props), {
    ref: function ref(e) {
      inputFocusRef.current = e;
      register(e);
    }
  }));
};

RHFTextarea.propTypes = {
  register: PropTypes.func,
  focus: PropTypes.bool,
  error: ERROR_PROP_SHAPE
};
RHFTextarea.Labeled = withLabel(RHFTextarea);

var _excluded$9 = ["register", "options", "name", "disabled", "readOnly", "size", "error"];

var _templateObject$2;
var StyledToggleButton = /*#__PURE__*/styled(ToggleButton).withConfig({
  componentId: "sc-cqhfcd-0"
})(["", ";"], function (_ref) {
  var error = _ref.error;
  return error && css(_templateObject$2 || (_templateObject$2 = _taggedTemplateLiteralLoose(["\n            border-color: ", ";\n        "])), token('colors.red600'));
}); // TODO ZHL-3967: a better solution than this

var stringifyBooleanIfNeeded = function stringifyBooleanIfNeeded(value) {
  return typeof value === 'boolean' ? String(value) : value;
};

var RHFToggleButtonGroup = function RHFToggleButtonGroup(_ref2) {
  var register = _ref2.register,
      options = _ref2.options,
      name = _ref2.name,
      disabled = _ref2.disabled,
      readOnly = _ref2.readOnly,
      size = _ref2.size,
      error = _ref2.error,
      props = _objectWithoutProperties(_ref2, _excluded$9);

  // ToggleButtonGroup does not render an input element, thus we can't leverage the callback ref pattern
  // used in other RHF components; instead, we manually register via useEffect
  useEffect(function () {
    register(name);
  }, [register, name]);
  return /*#__PURE__*/jsx(ToggleButtonGroup, Object.assign({}, props, {
    value: stringifyBooleanIfNeeded(props.value),
    defaultValue: stringifyBooleanIfNeeded(props.defaultValue),
    children: Object.entries(options).map(function (_ref3) {
      var _ref4 = _slicedToArray(_ref3, 2),
          value = _ref4[0],
          label = _ref4[1];

      return /*#__PURE__*/jsx(StyledToggleButton, {
        value: stringifyBooleanIfNeeded(value),
        type: "button",
        size: size,
        disabled: disabled || readOnly,
        error: error,
        children: label
      }, value);
    })
  }));
};

RHFToggleButtonGroup.propTypes = Object.assign({}, omit(ToggleButtonGroup.propTypes, ['children']), {
  value: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  defaultValue: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  register: PropTypes.func,
  options: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.string.isRequired]).isRequired),
  name: PropTypes.string.isRequired,
  id: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  error: PropTypes.bool
});
RHFToggleButtonGroup.Labeled = withLabel(RHFToggleButtonGroup);

var _excluded$a = ["readOnly", "options", "onChange", "onBlur", "register", "focus", "error", "name", "control"];
var StyledCombobox = /*#__PURE__*/styled(Combobox).withConfig({
  componentId: "sc-1t2o2zw-0"
})(readOnlyMixin());

var noop$2 = function noop() {};

var RHFCombobox = function RHFCombobox(_ref) {
  var readOnly = _ref.readOnly,
      options = _ref.options,
      _onChange = _ref.onChange,
      _onBlur = _ref.onBlur,
      register = _ref.register,
      focus = _ref.focus,
      error = _ref.error,
      name = _ref.name,
      control = _ref.control,
      props = _objectWithoutProperties(_ref, _excluded$a);

  var inputFocusRef = useRef();
  useFocus(focus, inputFocusRef);

  var noopWhenReadOnly = function noopWhenReadOnly(onEvent) {
    // don't override a function that doesn't exist
    if (typeof onEvent === 'function') {
      return readOnly ? noop$2 : onEvent;
    }

    return onEvent;
  };

  var getOnChangeBlur = function getOnChangeBlur() {
    return {
      onBlur: function onBlur(evt) {
        noopWhenReadOnly(_onBlur === null || _onBlur === void 0 ? void 0 : _onBlur({
          name: name,
          value: evt
        }));
      },
      onChange: function onChange(evt) {
        noopWhenReadOnly(_onChange === null || _onChange === void 0 ? void 0 : _onChange({
          name: name,
          value: evt
        }));
      }
    };
  };

  return /*#__PURE__*/jsx(Controller, {
    control: control,
    name: name,
    render: function render(_ref2) {
      var controlName = _ref2.name;
      return /*#__PURE__*/jsx(StyledCombobox, Object.assign({
        ref: function ref(e) {
          inputFocusRef.current = e;
          register(e);
        },
        readOnly: readOnly,
        options: options,
        error: !!error,
        name: controlName
      }, getOnChangeBlur(), props));
    }
  });
};

RHFCombobox.propTypes = {
  readOnly: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  value: PropTypes.any,
  error: ERROR_PROP_SHAPE,
  defaultValue: PropTypes.any,
  options: PropTypes.arrayOf(PropTypes.shape(SELECT_OPTION_SHAPE)).isRequired,
  register: PropTypes.func,
  name: PropTypes.string,
  focus: PropTypes.bool,
  control: PropTypes.object
};
RHFCombobox.EMPTY_OPTION = EMPTY_OPTION;
RHFCombobox.Labeled = withLabel(RHFCombobox);

var _excluded$b = ["field", "type", "children"],
    _excluded2$2 = ["label"];

var _COMPONENT_FOR_FIELD_;

var LabeledTextInput = function LabeledTextInput(props) {
  var Input = props.type === 'password' ? RHFPassword.Labeled : RHFInput.Labeled;
  return /*#__PURE__*/jsx(Input, Object.assign({}, props));
};

LabeledTextInput.propTypes = {
  type: PropTypes.string
};
var COMPONENT_FOR_FIELD_TYPE = (_COMPONENT_FOR_FIELD_ = {}, _COMPONENT_FOR_FIELD_[FIELD_TYPES.INPUT] = LabeledTextInput, _COMPONENT_FOR_FIELD_[FIELD_TYPES.SELECT] = RHFSelect.Labeled, _COMPONENT_FOR_FIELD_[FIELD_TYPES.COMBOBOX] = RHFCombobox.Labeled, _COMPONENT_FOR_FIELD_[FIELD_TYPES.RADIO_GROUP] = RHFRadioGroup, _COMPONENT_FOR_FIELD_[FIELD_TYPES.CHECKBOX] = RHFCheckbox, _COMPONENT_FOR_FIELD_[FIELD_TYPES.DATEPICKER] = RHFDatePicker.Labeled, _COMPONENT_FOR_FIELD_[FIELD_TYPES.TEXTAREA] = RHFTextarea.Labeled, _COMPONENT_FOR_FIELD_[FIELD_TYPES.TOGGLE_BUTTON_GROUP] = RHFToggleButtonGroup.Labeled, _COMPONENT_FOR_FIELD_[FIELD_TYPES.HIDDEN] = RHFInput, _COMPONENT_FOR_FIELD_);
/**
 * @param {Object} field - The field object generated via schema introspection
 * @param {Function} field.getFieldType - Function to retrieve which field type should be used to represent this field. An override field
 *                                        type can be passed into this function, and if valid, will be the returned field type. If the
 *                                        override field type is incompatible with this field, the derived field type will be returned instead
 * @param {Function} field.getFieldProps - Function to retrieve the props for the form field corresponding to the input field type. This
 *                                         function should be called with the result of the field.getFieldType to ensure that the requested
 *                                         field type is compatible with the field
 * @param {String} type - The override field type. This is not guaranteed to be the field type that is rendered (see above).
 */

var SchemaGeneratedField = function SchemaGeneratedField(_ref) {
  var field = _ref.field,
      overrideFieldType = _ref.type,
      children = _ref.children,
      props = _objectWithoutProperties(_ref, _excluded$b);

  // The field prop will be undefined when the corresponding schema field is stripped. We don't want to render a form field when
  // the corresponding schema field doesn't exist, thus we return null. This automatically un-registers the field from the form.
  if (typeof field === 'undefined') {
    return null;
  }

  var getFieldProps = field.getFieldProps,
      getFieldType = field.getFieldType;
  var fieldType = getFieldType(overrideFieldType);
  var Component = COMPONENT_FOR_FIELD_TYPE[fieldType];

  var _getFieldProps = getFieldProps(fieldType),
      label = _getFieldProps.label,
      fieldProps = _objectWithoutProperties(_getFieldProps, _excluded2$2);

  return /*#__PURE__*/jsx(Component, Object.assign({}, fieldProps, props, {
    children: children !== null && children !== void 0 ? children : label
  }));
};

var FIELD_SHAPE = {
  getFieldProps: PropTypes.func.isRequired,
  getFieldType: PropTypes.func.isRequired
};
SchemaGeneratedField.propTypes = {
  type: PropTypes.oneOf(Object.values(FIELD_TYPES)),
  field: PropTypes.shape(FIELD_SHAPE),
  children: PropTypes.node
};

export { EMPTY_OPTION, ERROR_PROP_SHAPE, FIELD_SHAPE, FIELD_TYPES, LabeledInput, Address as RHFAddress, RHFBooleanRadioGroup, RHFCheckbox, RHFCombobox, RHFDatePicker, RHFDownPayment, RHFInput, RHFRadioGroup, SchemaGeneratedField as RHFSchemaGeneratedField, RHFSelect, RHFTextarea, RHFToggleButtonGroup, ReadOnlyCapableAdornedInput, SELECT_OPTION_SHAPE, getSchemaGeneratedFields, inputReadOnly, objectToKeyValueOptions, readOnlyMixin, removeEmptyStrings, useAutoComplete, useFocus, useForm, useLayoutEffectInBrowser, withLabel };
